'use client'

import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef, useEffect } from 'react';
import { match } from 'ts-pattern';
import ForwardRef from '../../icons/ArrowLeftIcon.mjs';
import ForwardRef$1 from '../../icons/ArrowRightIcon.mjs';
import { Experimental } from '../../internal/lifecycles.mjs';
import defaultStyled from '../../internal/styled-components.mjs';
import { breakpoints, color } from '../../lib/styles/styles.mjs';

const BannerCarouselContainer = defaultStyled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  position: relative;
  padding: 1rem 0;

  @media ${breakpoints.up("s")} {
    gap: 1rem;
    padding: 1.5rem 0.5rem;
  }

  @media ${breakpoints.up("m")} {
    gap: 1rem;
    padding: 1.5rem 1rem;
  }
`;
const CarouselContainer = defaultStyled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
`;
const CarouselSwimlane = defaultStyled.div`
  display: flex;
  overflow: hidden;
  gap: 0.5rem;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  transition: transform 0.5s ease-in-out;

  @media ${breakpoints.up("m")} {
    gap: 1rem;
  }
`;
const CarouselItem = defaultStyled.div`
  display: flex;
  flex: 0 0 auto;
  width: 20.5rem;
  height: 7.5rem;
  justify-content: center;
  border-radius: 0.5rem;
  background-color: ${color("mineshaft40")};
  overflow: hidden;

  @media ${breakpoints.up("xs")} {
    width: 20.5rem;
    height: 7.5rem;
  }
  @media ${breakpoints.up("m")} {
    width: 24rem;
    height: 8.75rem;
  }
  @media ${breakpoints.up("xl")} {
    width: 26.125rem;
    height: 9.5rem;
  }
`;
const StyledSlideArrow = defaultStyled.div`
  height: 1.5rem;
  color: ${color("primary500")};
  cursor: pointer;
  font-size: 2rem;
  display: flex;
  align-items: center;
  border-radius: 50%;
  background-color: ${color("white")};
  justify-content: center;
  border: 0.0625rem solid ${color("mineshaft80")};
  box-sizing: border-box;
  box-shadow: 0 0.3125rem 0.625rem 0 rgba(0, 0, 0, 0.1);

  @media ${breakpoints.up("s")} {
    width: 2.5rem;
    height: 2.5rem;
  }

  &:hover {
    cursor: pointer;
    background-color: ${color("primary50")};
    border: 0.0625rem solid ${color("primary100")};
    color: ${color("primary500")};
  }

  &:active {
    cursor: pointer;
    background-color: ${color("primary50")};
    border: 0.125rem solid ${color("primary500")};
    color: ${color("primary500")};
  }
`;
const StyledArrowLeftIcon = defaultStyled(ForwardRef)`
  font-size: 1.5rem;
`;
const StyledArrowRightIcon = defaultStyled(ForwardRef$1)`
  font-size: 1.5rem;
`;
const ArrowContainer = defaultStyled.div`
  position: absolute;
  top: 42%;
  transform: translateY(-50%);
  z-index: 1;
  display: none;

  @media ${breakpoints.up("s")} {
    display: inherit;
    ${(props) => match(props.direction).with("left", () => "left: -0.8rem;").with("right", () => "right: -0.8rem;").exhaustive()}
  }

  @media ${breakpoints.up("m")} {
    display: inherit;
    ${(props) => match(props.direction).with("left", () => "left: -0.2rem;").with("right", () => "right: -0.2rem;").exhaustive()}
  }
`;
const CarouselIndicatorItem = defaultStyled.li`
  width: 0.5rem;
  height: 0.5rem;
  display: inline-block;
  margin-left: 0.5rem;
  border-radius: 50%;
  background-color: ${(props) => props.active ? "#1454B8" : "#CCCCCC"};
  cursor: pointer;
`;
const CarouselIndicatorList = defaultStyled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  height: auto;
  overflow: hidden;
  text-align: "center";
  z-index: 1;
`;
const CarouselItemSkeleton = defaultStyled.div`
  display: flex;
  flex: 0 0 auto;
  width: 20.5rem;
  height: 7.5rem;
  justify-content: center;
  border-radius: 0.5rem;
  overflow: hidden;
  background: linear-gradient(
    270deg,
    ${color("mineshaft80")} 0%,
    ${color("mineshaft40")} 49%,
    ${color("mineshaft80")} 100%
  );

  @media ${breakpoints.up("xs")} {
    width: 20.5rem;
    height: 7.5rem;
  }
  @media ${breakpoints.up("m")} {
    width: 24rem;
    height: 8.75rem;
  }
  @media ${breakpoints.up("xl")} {
    width: 26.125rem;
    height: 9.5rem;
  }
`;
const CarouselItemImage = defaultStyled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const BannerCarousel = ({
  images = [],
  rotationInterval,
  onBannerClick,
  setBannerRef,
  loading
}) => {
  const [carouselImages, setImages] = useState([...images]);
  if (!carouselImages || carouselImages.length === 0) {
    return null;
  }
  const carouselRef = useRef(null);
  const [autoSlide, setAutoSlide] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const handleTouchStart = (e) => {
    e?.targetTouches?.length > 0 && setTouchStart(e.targetTouches[0].clientX);
  };
  const handleTouchEnd = (e) => {
    const currentTouchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - currentTouchEnd;
    if (e.target.nodeName === "IMG" && Math.abs(diff) < 20) {
      return;
    }
    if (Math.abs(diff) > 20) {
      if (diff > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
  };
  useEffect(() => {
    if (autoSlide) {
      clearTimeout(autoSlide);
    }
    let timer = null;
    if (rotationInterval && rotationInterval > 0) {
      timer = setInterval(() => {
        nextSlide();
      }, rotationInterval);
      setAutoSlide(timer);
    }
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [currentSlide, carouselImages]);
  const nextSlide = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0;
    }
    if (autoSlide) {
      clearTimeout(autoSlide);
    }
    setImages((prevImages) => {
      const firstImage = prevImages.shift();
      prevImages.push(firstImage);
      return [...prevImages];
    });
    setCurrentSlide((prevSlide2) => (prevSlide2 + 1) % carouselImages.length);
  };
  const prevSlide = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0;
    }
    if (autoSlide) {
      clearTimeout(autoSlide);
    }
    setImages((prevImages) => {
      const lastImage = prevImages.pop();
      prevImages.unshift(lastImage);
      return [...prevImages];
    });
    setCurrentSlide(
      (prevSlide2) => (prevSlide2 - 1 + carouselImages.length) % carouselImages.length
    );
  };
  const handleCurrentSlideRotation = (index) => {
    setImages(() => {
      const newImages = [...images];
      const rotatedImages = newImages.splice(index).concat(newImages);
      return rotatedImages;
    });
    setCurrentSlide(index);
  };
  return /* @__PURE__ */ jsx(Experimental, { name: "BannerCarousel", dependencies: { Skeleton: true }, children: /* @__PURE__ */ jsxs(BannerCarouselContainer, { "aria-label": "Banner Carousel", role: "region", children: [
    /* @__PURE__ */ jsx(
      ArrowContainer,
      {
        direction: "left",
        onClick: prevSlide,
        onKeyDown: prevSlide,
        tabIndex: 0,
        role: "button",
        "aria-label": "Previous slide",
        children: /* @__PURE__ */ jsx(StyledSlideArrow, { direction: "left", children: /* @__PURE__ */ jsx(StyledArrowLeftIcon, { "aria-label": "Left Arrow" }) })
      }
    ),
    /* @__PURE__ */ jsx(CarouselContainer, { children: /* @__PURE__ */ jsx(
      CarouselSwimlane,
      {
        ref: carouselRef,
        onTouchStart: handleTouchStart,
        onTouchEnd: (e) => handleTouchEnd(e),
        children: loading ? images.map((image) => /* @__PURE__ */ jsx(
          CarouselItemSkeleton,
          {
            "aria-label": "carousel skeleton"
          },
          typeof image === "string" ? image : image.imageUrl
        )) : carouselImages.map((image, index) => {
          if (typeof image === "string") {
            return /* @__PURE__ */ jsx(CarouselItem, { children: /* @__PURE__ */ jsx(
              CarouselItemImage,
              {
                src: image,
                alt: `Banner Image ${index + 1} in Banner Carousel`,
                onClick: (e) => onBannerClick?.(index, e),
                ref: setBannerRef?.(index)
              }
            ) }, `${image}-${index}`);
          } else {
            return /* @__PURE__ */ jsx(CarouselItem, { children: /* @__PURE__ */ jsx(
              "a",
              {
                href: image.link,
                "aria-label": `Link to ${image.link}`,
                children: /* @__PURE__ */ jsx(
                  CarouselItemImage,
                  {
                    src: image.imageUrl,
                    alt: `Banner Image ${index + 1} in Banner Carousel with link`,
                    onClick: (e) => onBannerClick?.(index, e),
                    ref: setBannerRef?.(index)
                  }
                )
              }
            ) }, `${image.imageUrl}-${index}`);
          }
        })
      }
    ) }),
    /* @__PURE__ */ jsx(
      ArrowContainer,
      {
        direction: "right",
        onClick: nextSlide,
        onKeyDown: nextSlide,
        tabIndex: 0,
        role: "button",
        "aria-label": "Next slide",
        children: /* @__PURE__ */ jsx(StyledSlideArrow, { direction: "right", children: /* @__PURE__ */ jsx(StyledArrowRightIcon, { "aria-label": "Right Arrow" }) })
      }
    ),
    /* @__PURE__ */ jsx(CarouselIndicatorList, { children: carouselImages.map((image, index) => /* @__PURE__ */ jsx(
      CarouselIndicatorItem,
      {
        onClick: () => handleCurrentSlideRotation(index),
        onKeyDown: (event) => {
          if (event.key === "Enter") {
            handleCurrentSlideRotation(index);
          }
        },
        active: index === currentSlide,
        tabIndex: 0,
        "aria-label": `Go to slide ${index + 1}`
      },
      typeof image === "string" ? `${image}-${index}` : `${image.imageUrl}-${index}`
    )) })
  ] }) });
};
BannerCarousel.displayName = "BannerCarousel";

export { BannerCarousel };
//# sourceMappingURL=BannerCarousel.mjs.map
